
// @flow
import React, { PureComponent } from 'react';
import { isEqual } from 'lodash';

import { withHelmet } from 'infrastructure/seo/Helmet';
import { getMetaBySlug } from 'utils/seoUtils';

import RencontreStatRow from 'components/rencontre/stats/RencontreStatRow';
import RencontreStatRowSimple from 'components/rencontre/stats/RencontreStatRowSimple';
import RankingTable from 'components/fragments/RankingTable';
import BonusCard from 'components/fragments/card/BonusCard';
import Loading from 'components/fragments/Loading';
import { ffrName } from 'utils/ffrNameUtils';

import { STATUS_SUCCESS } from 'constants/statusConstants';

import type { Status } from 'types/Status';
import type { RencontreType } from 'types/Rencontre';
import { type ResultDetailType, nullResultDetail } from 'types/Result';
import { type CompetitionRankingType } from 'types/Ranking';

export type StateProps = {
  rencontre: ?RencontreType,
  ranking: CompetitionRankingType[],
  status: Status,
}

type State = {
  ourClubResults: ResultDetailType,
};

class RencontreStats extends PureComponent<StateProps, State> {
  state = {
    ourClubResults: nullResultDetail,
  };

  componentDidMount() {
    const { rencontre } = this.props;

    if (rencontre) {
      const {
          resultLocal,
          resultVisiteur,
      } = rencontre;

      const ourClubResults = [resultLocal, resultVisiteur].filter((results) => results.ourClub)[0];
      this.setState({ ourClubResults });
    }
  }

  renderRencontreStatBox = () => {
    const { rencontre } = this.props;

    if (!rencontre) {
      return null;
    }

    let isLocal = true;
    if (rencontre.resultVisiteur.ourClub) {
      isLocal = false;
    }

    const { actions } = rencontre;

    return actions
      .filter((action) => !action.type.includes('remplacement'))
      .map(({ id, type, localActions, visiteurActions }) => {
      return (
        <RencontreStatRow
          key={`statbox_${type}`}
          localActionsNumber={localActions.length}
          visiteurActionNumber={visiteurActions.length}
          localActions={localActions}
          visiteurActions={visiteurActions}
          actionType={type}
          isLocal={isLocal}
        />
      );
    });
  };

  renderRencontreStatBoxWithoutActions = () => {
    const { rencontre } = this.props;

    if (!rencontre) {
      return null;
    }

    let isLocal = true;
    if (rencontre.resultVisiteur.ourClub) {
      isLocal = false;
    }
    const actionsTotal:Array<any> = [
      {
        type: 'essai', 
        localActions: rencontre.resultLocal.essais ? rencontre.resultLocal.essais : 0,
        visiteurActions: rencontre.resultVisiteur.essais ? rencontre.resultVisiteur.essais : 0,
      },
      {
        type: 'essai de penalite',
        localActions: rencontre.resultLocal.essaisDePenalite ? rencontre.resultLocal.essaisDePenalite : 0,
        visiteurActions: rencontre.resultVisiteur.essaisDePenalite ? rencontre.resultVisiteur.essaisDePenalite : 0,
      },
      {
        type: 'transformation',
        localActions: rencontre.resultLocal.butsApresEssai ? rencontre.resultLocal.butsApresEssai : 0,
        visiteurActions: rencontre.resultVisiteur.butsApresEssai ? rencontre.resultVisiteur.butsApresEssai : 0,
      },
      {
        type: 'drop',
        localActions: rencontre.resultLocal.dropGoals ? rencontre.resultLocal.dropGoals : 0,
        visiteurActions: rencontre.resultVisiteur.dropGoals ? rencontre.resultVisiteur.dropGoals : 0,
      },
      {
        type: 'penalite',
        localActions: rencontre.resultLocal.butsPenalite ? rencontre.resultLocal.butsPenalite : 0,
        visiteurActions: rencontre.resultVisiteur.butsPenalite ? rencontre.resultVisiteur.butsPenalite : 0,
      },
      {
        type: 'carton rouge',
        localActions: rencontre.resultLocal.cartonsRouges ? rencontre.resultLocal.cartonsRouges : 0,
        visiteurActions: rencontre.resultVisiteur.cartonsRouges ? rencontre.resultVisiteur.cartonsRouges : 0,
      }
    ];

    return actionsTotal
      .filter((action) => !(action.type === 'carton rouge' && action.localActions === 0 && action.visiteurActions === 0))
      .map<any>((data:any) => {
        return (
          <RencontreStatRowSimple
            key={`statbox_${data.type}`}
            localActions={data.localActions}
            visiteurActions={data.visiteurActions}
            actionType={data.type}
            isLocal={isLocal}
          />
        );
      });
  };

  renderRanking = () => {
    const { ranking } = this.props;

    return ranking.map(({ id, classement }) => <RankingTable key={id} classement={classement} />);
  };

  render() {
    const { ourClubResults } = this.state;
    const { rencontre, status, ranking } = this.props;

    if (!rencontre) {
      return <Loading />;
    }

    const {
      dateEffective,
      localStructure: {
        nom: localNom,
      },
      visiteurStructure: {
        nom: visiteurNom,
      },
      competition: {
        nom: competitionNom,
      },
      actions: actionsFiltered,
    } = rencontre;

    const saison = dateEffective.getFullYear();

    const ourClubResultsIsHydrated = isEqual(ourClubResults, nullResultDetail);

    if (status !== STATUS_SUCCESS && !ourClubResultsIsHydrated) {
      return <Loading isWhite />;
    }

    const {
      bonusDefensif,
      bonusOffensif,
    } = ourClubResults || { bonusOffensif: 0, bonusDefensif: 0 };

    const hasRanking = ranking && ranking.length > 0 && ranking[0].classement && ranking[0].classement.length > 0;
    const hasActions = actionsFiltered
      .filter((action) => action.localActions.length !== 0 && action.visiteurActions.length !== 0).length > 0;
    const lc = 'link-chevron';

    return (
      <>
      <h2 className="mt-3">{`Statistiques ${localNom} - ${visiteurNom} saison ${saison}`}</h2>
      <div className='page'>
        {hasActions && (
          <div className='row mt-3'>
            <div className='col col-lg-8on9'>
              <h3 className='mb-3'>Statistiques du match</h3>
              <div className='stats-match'>
                <div className='stats-match__header'>
                  <span>{ffrName(localNom)}</span>
                  <span>{ffrName(visiteurNom)}</span>
                </div>
                <div className='stats-match__body'>
                  <hr />
                  {this.renderRencontreStatBox()}
                </div>
              </div>
            </div>
          </div>
        )}
        {!hasActions && rencontre.etat === 'Score validé' && (
          <div className='row mt-3'>
            <div className='col col-lg-8on9'>
              <h3 className='mb-3'>Statistiques du match</h3>
              <div className='stats-match'>
                <div className='stats-match__header'>
                  <span>{ffrName(localNom)}</span>
                  <span>{ffrName(visiteurNom)}</span>
                </div>
                <div className='stats-match__body'>
                  <hr />
                  {this.renderRencontreStatBoxWithoutActions()}
                </div>
              </div>
            </div>
          </div>
        )}

        {hasRanking && (
          <div className='row mt-5 mt-md-7'>
            <div className='col col-lg-8on9'>
              <h3 className='mb-3'>{competitionNom}</h3>
              {this.renderRanking()}
              <a
                href={`https://competitions.ffr.fr/${ranking[0].saison ? ranking[0].saison.nom : ''}/competitions/${ranking[0].identifiant}/classements.html` || 'https://competitions.ffr.fr/'}
                target="_blank"
                rel="noopener noreferrer"
                className={`${lc} ${lc}--blue ${lc}--inline ${lc}--external`}
              >
                <span>Voir le classement complet</span>
                <i className="icon icon-external"/>
              </a>
            </div>
          </div>
        )}
        {rencontre.etat === 'Score validé' && (
        <div className='row mt-5 mt-md-7'>
          <div className='col col-lg-8on9'>
            <h3 className='mb-3'>Bonus</h3>
            <div className='row'>
              <div className="col col-md-6">
                <BonusCard bonus={bonusDefensif} isDefensive classNames='mb-2 mb-md-0' />
              </div>
              <div className="col col-md-6">
                <BonusCard bonus={bonusOffensif} />
              </div>
            </div>
          </div>
        </div>)}
        <hr className='separator mt-4 mt-md-5 mt-lg-7 mb-4' />
      </div>
      </>
    );
  }
}

export default withHelmet(RencontreStats)((Helmet, pageProps) => {
  const { rencontre } = pageProps;

  const teamA = rencontre.localStructure.nom;
  const teamB = rencontre.visiteurStructure.nom;

  const meta = getMetaBySlug('match/stats');

  return (
    <Helmet>
      <title>{meta.title(teamA, teamB)}</title>
      {
        meta.description()
        ? <meta name={'description'} content={meta.description()} />
        : ''
      }
      <meta name={'keywords'} content={meta.keywords(teamA, teamB)} />
      <meta property={'og:url'} content={window.location.href} />
      <meta property={'og:title'} content={meta.title(teamA, teamB)} />
      {
        meta.description()
        ? <meta property={'og:description'} content={meta.description()} />
        : ''
      }
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
